import { supabase } from '../lib/supabase';

export const paymentService = {
  // Create payment intent for travel booking
  async createPaymentIntent(orderData, customerInfo) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          orderData,
          customerInfo
        }
      });

      if (error) {
        console.error('Payment intent creation error:', error);
        throw new Error(error.message || 'Failed to create payment intent');
      }
      
      return data;
    } catch (error) {
      console.error('Payment service error:', error);
      throw error;
    }
  },

  // Confirm payment after Stripe processing
  async confirmPayment(paymentIntentId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
        body: { paymentIntentId }
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        throw new Error(error.message || 'Failed to confirm payment');
      }
      
      return data;
    } catch (error) {
      console.error('Payment confirmation service error:', error);
      throw error;
    }
  },

  // Create SetupIntent for card-on-file
  async createSetupIntent(agentId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-setup-intent', {
        body: { agentId }
      });

      if (error) {
        console.error('SetupIntent creation error:', error);
        throw new Error(error.message || 'Failed to create setup intent');
      }
      
      return data;
    } catch (error) {
      console.error('Setup intent service error:', error);
      throw error;
    }
  },

  // Get agent billing status
  async getAgentBillingStatus(agentId) {
    try {
      // Use select('*') to avoid selecting non-existent columns
      const { data, error } = await supabase
        ?.from('agent_profiles')
        ?.select('*')
        ?.eq('user_id', agentId)
        ?.maybeSingle();

      if (error) {
        console.error('Failed to fetch agent billing status:', error);
        return { billing_ready: false, billing_status: 'needs_card' };
      }
      
      // Return only the billing-related fields from the fetched data
      return {
        billing_ready: data?.billing_ready || false,
        billing_status: data?.billing_status || 'needs_card',
        last_payment_error: data?.last_payment_error || null,
        stripe_payment_method_id: data?.stripe_payment_method_id || null
      };
    } catch (error) {
      console.error('Failed to fetch agent billing status:', error);
      return { billing_ready: false, billing_status: 'needs_card' };
    }
  },

  // Get user's saved payment methods
  async getSavedPaymentMethods(userId) {
    try {
      const { data, error } = await supabase?.from('payment_methods')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      return [];
    }
  },

  // Get order details with items
  async getOrderDetails(orderId) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          order_items (
            *,
            travel_packages (name, destination),
            package_variants (name, departure_date, return_date)
          ),
          payment_transactions (*)
        `)?.eq('id', orderId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      throw error;
    }
  },

  // Get user's orders
  async getUserOrders(userId, limit = 10) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          order_items (
            *,
            travel_packages (name, destination)
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch user orders:', error);
      return [];
    }
  },

  // Update order status
  async updateOrderStatus(orderId, status, paymentStatus = null) {
    try {
      const updateData = { status };
      if (paymentStatus) {
        updateData.payment_status = paymentStatus;
      }

      const { data, error } = await supabase?.from('orders')?.update(updateData)?.eq('id', orderId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to update order status:', error);
      throw error;
    }
  },

  // Format amount for display (handles INR and other currencies)
  formatAmount(amount, currency = 'INR') {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    if (isNaN(numericAmount)) return '₹0';
    
    // Convert from paisa to rupees for INR
    const displayAmount = currency === 'INR' ? numericAmount / 100 : numericAmount;
    
    const formatters = {
      'INR': new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }),
      'USD': new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }),
      'GBP': new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      })
    };

    const formatter = formatters?.[currency] || formatters?.['INR'];
    return formatter?.format(displayAmount);
  },

  // Validate customer info for Indian compliance
  validateCustomerInfo(customerInfo) {
    const errors = [];
    
    if (!customerInfo?.firstName?.trim()) {
      errors?.push('First name is required');
    }
    
    if (!customerInfo?.lastName?.trim()) {
      errors?.push('Last name is required');
    }
    
    if (!customerInfo?.email?.trim()) {
      errors?.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(customerInfo?.email)) {
      errors?.push('Valid email is required');
    }
    
    if (!customerInfo?.billing?.address_line_1?.trim()) {
      errors?.push('Billing address is required');
    }
    
    if (!customerInfo?.billing?.city?.trim()) {
      errors?.push('City is required');
    }
    
    if (!customerInfo?.billing?.state?.trim()) {
      errors?.push('State is required');
    }
    
    if (!customerInfo?.billing?.postal_code?.trim()) {
      errors?.push('Postal code is required');
    }
    
    return errors;
  },

  // Calculate booking totals
  calculateBookingTotal(items, taxRate = 0.18, serviceFeeRate = 0.05) {
    const subtotal = items?.reduce((total, item) => {
      return total + (item?.unit_price * item?.traveler_count);
    }, 0);
    
    const serviceFee = Math.round(subtotal * serviceFeeRate);
    const taxAmount = Math.round((subtotal + serviceFee) * taxRate);
    const total = subtotal + serviceFee + taxAmount;
    
    return {
      subtotal,
      serviceFee,
      taxAmount,
      total,
      currency: 'INR'
    };
  },

  // Generate order number
  generateOrderNumber() {
    const timestamp = Date.now()?.toString();
    const random = Math.random()?.toString(36)?.substring(2, 8)?.toUpperCase();
    return `HY-${timestamp?.slice(-8)}-${random}`;
  },

  // Charge saved card for enquiry acceptance (off_session)
  async chargeCardOnFile(orderData, agentInfo) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          orderData,
          customerInfo: {
            userId: agentInfo?.id,
            firstName: agentInfo?.full_name?.split(' ')?.[0] || 'Agent',
            lastName: agentInfo?.full_name?.split(' ')?.slice(1)?.join(' ') || 'User',
            email: agentInfo?.email,
            phone: agentInfo?.phone || '',
            stripeCustomerId: agentInfo?.stripe_customer_id || null,
            billing: {
              address_line_1: agentInfo?.business_address || '123 Business Street',
              address_line_2: '',
              city: agentInfo?.location || 'London',
              state: 'England',
              postal_code: 'SW1A 1AA',
              country: 'GB'
            }
          },
          useCardOnFile: true // Flag to use saved card with off_session
        }
      });

      if (error) {
        console.error('Card on file charge error:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        // Try to extract more detailed error message
        const errorMessage = error?.message || error?.msg || JSON.stringify(error);
        throw new Error(errorMessage);
      }
      
      return data;
    } catch (error) {
      console.error('Card on file charge service error:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      throw error;
    }
  },

  // Wrapper for enquiry acceptance payment (maintains backward compatibility)
  async createEnquiryAcceptancePayment(agentId, enquiryId) {
    try {
      console.log('[paymentService] Creating enquiry acceptance payment:', { agentId, enquiryId });
      console.log('[paymentService] Fetching agent profile for user_id:', agentId);

      // Fetch agent profile - use only valid columns from agent_profiles table
      const { data: agentProfile, error: profileError } = await supabase
        ?.from('agent_profiles')
        ?.select('*')
        ?.eq('user_id', agentId)
        ?.maybeSingle();

      console.log('[paymentService] Agent profile query result:', { 
        user_id: agentId,
        profile_found: !!agentProfile, 
        error: profileError,
        error_code: profileError?.code,
        error_message: profileError?.message
      });

      // Handle missing profile gracefully based on email verification
      if (profileError || !agentProfile) {
        console.error('[paymentService] Failed to fetch agent profile:', profileError);
        
        // Get current user to check email verification status
        const { data: { user } } = await supabase?.auth?.getUser();
        
        if (user && !user?.email_confirmed_at) {
          // Email not verified
          throw new Error('Please verify your email to complete agent setup before purchasing enquiries.');
        } else {
          // Email verified but profile missing
          throw new Error('Your agent profile could not be loaded. Please contact support.');
        }
      }

      // Create order data for enquiry acceptance fee (£7.00 = 700 pence)
      const orderData = {
        amount: 700, // £7.00 in pence
        currency: 'GBP',
        description: `Enquiry acceptance fee for enquiry #${enquiryId}`,
        total: 7.00, // Total amount in pounds
        items: [{
          name: 'Enquiry Access Fee',
          unit_price: 7.00,
          traveler_count: 1,
          total_price: 7.00
        }],
        enquiry_id: enquiryId,
        metadata: {
          type: 'enquiry_acceptance',
          enquiry_id: enquiryId,
          agent_id: agentId
        }
      };

      console.log('[paymentService] Order data prepared:', orderData);

      // Call the existing chargeCardOnFile function with available agent_profiles data
      const result = await this.chargeCardOnFile(orderData, {
        id: agentProfile?.user_id,
        full_name: 'Agent User', // Placeholder - not available in agent_profiles
        email: 'agent@example.com', // Placeholder - not available in agent_profiles
        phone: '', // Not available in agent_profiles
        business_address: agentProfile?.business_address,
        location: 'UK', // Placeholder - not available in agent_profiles
        stripe_customer_id: agentProfile?.stripe_customer_id
      });

      console.log('[paymentService] Enquiry acceptance payment result:', result);
      return result;
    } catch (error) {
      console.error('[paymentService] Enquiry acceptance payment error:', error);
      throw error;
    }
  }
};

export default paymentService;